/**
 * Bronmails bij een 📧-voorstel (Joris, 11 okt 2026): "op de mail kunnen klikken om te zien welke mail ik
 * gestuurd heb". Per bronmail een klein ✉-chipje (onderwerp + tijd; dezelfde draad samen als "2×").
 * Klik → popover met aan, tijd, onderwerp en de eerste ±600 tekens, plus "Open in Outlook".
 *
 * De inhoud wordt pas bij klikken opgehaald: eerst via de eigen route (server leest alleen jouw mailbox,
 * adres uit je login). Lukt dat niet (je valt buiten de toegangsgroep van de mail-app), dan leest de
 * browser de mail zelf met je eigen Mail.Read-token. Er wordt niets bewaard.
 */
import React, { useState } from 'react';
import { Box, Button, Chip, CircularProgress, Popover, Typography } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { authFetch } from '../../utils/authHelpers';
import { BACKEND_URL } from '../../utils/apiConfig';
import { acquireGraphToken } from '../../utils/graphToken';

const VOORVOEGSEL = /^\s*((re|fw|fwd|wg|antw|aw|tr)\s*:\s*)+/i;
export const kaalOnderwerp = (s) => String(s || '').replace(VOORVOEGSEL, '').trim();

/** 'Re: X' en 'RE: X' horen bij dezelfde draad: één chip met "2×". Volgorde blijft die van de mails. */
export function groepeerBronnen(links) {
    const groepen = [];
    for (const l of Array.isArray(links) ? links : []) {
        if (!l || (!l.id && !l.url)) continue;
        const sleutel = kaalOnderwerp(l.subject).toLowerCase() || l.id || l.url;
        const g = groepen.find((x) => x.sleutel === sleutel);
        if (g) g.mails.push(l); else groepen.push({ sleutel, mails: [l] });
    }
    return groepen;
}

export function tijdVan(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Amsterdam' });
}

async function viaGraph(id) {
    const token = await acquireGraphToken(['Mail.Read']);
    const r = await fetch(`https://graph.microsoft.com/v1.0/me/messages/${encodeURIComponent(id)}?$select=id,subject,toRecipients,ccRecipients,sentDateTime,body,webLink`, {
        headers: { Authorization: `Bearer ${token}`, Prefer: 'IdType="ImmutableId", outlook.body-content-type="text"' },
    });
    if (!r.ok) throw new Error(r.status === 404 ? 'Mail niet meer gevonden' : `Outlook ${r.status}`);
    const m = await r.json();
    const tekst = String(m.body?.content || '').replace(/\s+/g, ' ').trim();
    const aan = [...(m.toRecipients || []), ...(m.ccRecipients || [])]
        .map((x) => x.emailAddress?.name || x.emailAddress?.address || '').filter(Boolean);
    return { subject: m.subject || '', sentDateTime: m.sentDateTime, to: [...new Set(aan)].slice(0, 10),
        preview: tekst.length > 600 ? `${tekst.slice(0, 600)}…` : tekst, webLink: m.webLink || null };
}

export async function haalBronmail(id) {
    try {
        const r = await authFetch(`${BACKEND_URL}/Api/slim-uren/mail-bron?id=${encodeURIComponent(id)}`);
        if (r.ok) return await r.json();
        if (r.status === 404) throw new Error('Mail niet meer gevonden');
    } catch (e) {
        if (e.message === 'Mail niet meer gevonden') throw e;
    }
    return viaGraph(id);   // geen servertoegang tot je mailbox: lees hem zelf (eigen token)
}

function BronPopover({ anker, groep, onSluit }) {
    const eerste = groep?.mails?.[0] || {};
    const [mail, setMail] = useState(null);
    const [fout, setFout] = useState(null);
    const [bezig, setBezig] = useState(false);
    const geladenVoor = React.useRef(null);

    React.useEffect(() => {
        if (!anker || !eerste.id || geladenVoor.current === eerste.id) return;
        geladenVoor.current = eerste.id;
        let actief = true;
        setBezig(true); setFout(null); setMail(null);
        haalBronmail(eerste.id)
            .then((m) => { if (actief) setMail(m); })
            .catch((e) => { if (actief) setFout(e.message || 'Mail ophalen mislukt'); })
            .finally(() => { if (actief) setBezig(false); });
        return () => { actief = false; };
    }, [anker, eerste.id]);

    const link = mail?.webLink || eerste.url;
    const aan = mail?.to?.length ? mail.to : (eerste.toRecipients || []);
    return (
        <Popover open={Boolean(anker)} anchorEl={anker} onClose={onSluit}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            slotProps={{ paper: { sx: { p: 2, width: 380, maxWidth: 'calc(100vw - 32px)' } } }}>
            <Typography sx={{ fontWeight: 700, fontSize: 13, mb: 0.5 }}>{mail?.subject || eerste.subject || 'Verzonden mail'}</Typography>
            <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>
                {aan.length ? `Aan ${aan.join(', ')}` : ''}
                {(mail?.sentDateTime || eerste.sentDateTime) ? ` · ${tijdVan(mail?.sentDateTime || eerste.sentDateTime)}` : ''}
                {groep?.mails?.length > 1 ? ` · ${groep.mails.length} mails in deze draad` : ''}
            </Typography>
            <Box sx={{ mt: 1.25, mb: 1.5, minHeight: 24 }}>
                {bezig && <CircularProgress size={16} />}
                {fout && <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{fout}</Typography>}
                {mail && (
                    <Typography sx={{ fontSize: 12.5, whiteSpace: 'pre-wrap', color: 'text.primary' }}>
                        {mail.preview || '(geen tekst)'}
                    </Typography>
                )}
                {!eerste.id && !bezig && (
                    <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>Voorbeeld niet beschikbaar voor dit voorstel.</Typography>
                )}
            </Box>
            {link && (
                <Button size="small" variant="outlined" component="a" href={link} target="_blank" rel="noopener noreferrer"
                    startIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />} sx={{ textTransform: 'none' }}>
                    Open in Outlook
                </Button>
            )}
        </Popover>
    );
}

export default function MailBronnen({ links }) {
    const [open, setOpen] = useState(null);   // { anker, groep }
    const groepen = groepeerBronnen(links);
    if (!groepen.length) return null;
    return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5, pl: { xs: 0, sm: 5 } }}>
            {groepen.map((g) => {
                const m = g.mails[0];
                const tijd = tijdVan(m.sentDateTime);
                const label = `✉ ${kaalOnderwerp(m.subject) || 'mail'}${tijd ? ` · ${tijd}` : ''}${g.mails.length > 1 ? ` · ${g.mails.length}×` : ''}`;
                return (
                    <Chip key={g.sleutel} size="small" variant="outlined" clickable label={label}
                        aria-label={`Bekijk verzonden mail: ${m.subject || 'mail'}`}
                        onClick={(e) => { e.stopPropagation(); setOpen({ anker: e.currentTarget, groep: g }); }}
                        sx={{ height: 20, fontSize: 10.5, maxWidth: 280, color: 'text.secondary', borderColor: 'divider' }} />
                );
            })}
            {open && <BronPopover key={open.groep.sleutel} anker={open.anker} groep={open.groep} onSluit={() => setOpen(null)} />}
        </Box>
    );
}
